<template>
    <md-card class="ql-page" id="ql-page">
        <md-dialog :md-open-from="`cardCover${pageNumber}`" :md-close-to="`cardCover${pageNumber}`" 
            ref="no-layers-dialog">
            <md-dialog-title>You don't have a current layer!</md-dialog-title>
            <md-dialog-content>Open up the layers menu and choose a layer to edit.</md-dialog-content>
            <md-dialog-actions>
                <md-button class="md-primary" @click="closeDialog('no-layers-dialog')">Ok</md-button>
            </md-dialog-actions>
        </md-dialog>
        <md-card-media-cover :id="`cardCover${pageNumber}`" ref="cardCover" class="ql-page-cover">
            <project-pdf-page class="ql-pdf-page" :class="{'night-mode': showMode, 'disable-text': disableText}"
                v-if="ready"
                :page="page" 
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @pdfLoaded="pdfLoaded"
                @rightclick.stop.prevent="rightClick"
                @cueCreated="cueCreated"
            />
            <p v-if="!ready">Loading PDF...</p>
            <resize-sensor @resize="resize"></resize-sensor>
        </md-card-media-cover>
        <div class="marker-container" v-if="pageReady" :style="{left: `${offsetX}px`, width: `${pdfWidth}px`}">
            <cue-position-marker v-for="cue in positionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="isCueEditable(cue)"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @updateCue="updateCue"
                @activated="disableText = true"
                @deactivated="disableText = false">
            </cue-position-marker>
            <cue-selection-marker v-for="cue in selectionCues"
                :cue="cue"
                :key="`${cue.id}${versionID}`"
                :editable="isCueEditable(cue)"
                :locked="locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @updateCue="updateCue"
                @activated="disableText = true"
                @deactivated="disableText = false">
            </cue-selection-marker>            
        </div>
        <project-cues-add-cue-button class="ql-add-cue-button" v-if="addingCue" 
            @cancel="cancelAddCue" 
            @addText="addText"
            @addCue="addCue"
            :cueToAdd="cueToAdd"
            :style="{top: `${addCueY}px`, left: `${addCueX}px`}" />

        <div class="ql-note-container" v-if="pageReady" :style="{left: `${offsetX}px`, width: `${pdfWidth}px`}">
            <project-note v-for="note in displayNotes" 
                :key="`${note.id}${versionID}`"
                :note="note"
                :locked="!isNoteEditable(note) || locked"
                :pageWidth="pageWidth"
                :pageHeight="pageHeight"
                @updateNote="updateNote"
                @activated="disableText = true"
                @deactivated="disableText = false">
            </project-note>
        </div>
    </md-card>
</template>

<script>
// Vendor
import resizeSensor from 'vue-resize-sensor';
import { mapGetters } from 'vuex';
// import Utils from '@/lib/Utils';
// Component
import CuePositionMarker from '@/components/project/cues/cue-position-marker';
import CueSelectionMarker from '@/components/project/cues/cue-selection-marker';
import ProjectCuesAddCueButton from '@/components/project/cues/add-cue-button';
import ProjectCueList from '@/components/project/cues/cue-list';
import ProjectPdfPage from '@/components/project/pdf-page';
import ProjectNotesNote from '@/components/project/notes/note';

const ProjectPage = {
    name: 'projectPage',
    props: ['page', 'layers', 'currentLayer', 'cues', 'locked', 'showMode'],
    data: function() {
        return {
            pageNumber: this.$props.page.pageNumber,
            pageContainerId: 'pageContainer' + this.$props.page.pageNumber,
            ready: false,
            pageReady: false,
            disableText: false,
            scale: 1,
            addingCue: false,
            addCueX: 0,
            addCueY: 0,
            cueToAdd: null,
            offsetX: 0,
            offsetY: 0,
            pageWidth: 0,
            pageHeight: 0,
            pdfHeight: 0,
            pdfWidth: 0,
            originWidth: 0,
            originHeight: 0,
            cueMarkerWidth: 24,
            cueMarkerHeight: 24
        };
    },
    computed: {
        ...mapGetters([
            'currentVersion',
            'layerByCue',
            'visibleCues',
            'editableLayers',
            'notesByPage'
        ]),
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        displayCues: function() {
            return this.visibleCues(this.pageNumber);
        },
        selectionCues: function() {
            return this.displayCues.filter(c => c.type === 'selection');
        },
        positionCues: function() {
            return this.displayCues.filter(c => c.type === 'position');
        },
        displayNotes: function() {
            return this.notesByPage(this.pageNumber);
        }
    },
    methods: {
        openDialog(ref) {
            this.$refs[ref].open();
        },
        closeDialog(ref) {
            this.$refs[ref].close();
        },
        getPDFViewport() {
            return this.page.pdfPage.getViewport(this.scale, 0);
        },
        readyPage() {
            this.ready = false;
            const bounds = this.$el.querySelector('.ql-page-cover').getBoundingClientRect();
            const vp = this.getPDFViewport();

            let ph = window.innerHeight - bounds.top;

            // let pw = Math.min(bounds.width, vp.width);

            
            let pw = (vp.width * ph) / vp.height; // correct one

            if (pw > bounds.width) {
                ph *= bounds.width / pw;
                pw = bounds.width;
            }
            // console.log(vp);

            this.scale = pw / Math.min(bounds.width, vp.width);

            this.originWidth = vp.width;
            this.originHeight = vp.height;

            // let ratio = 1;

            // if (ph < pw) {
            //     ratio = ph / vp.height;
            // } else if (ph > pw) {
            //     ratio = pw / vp.width;
            // }

            // console.log(`ratio: ${ratio}, width: ${pw}, height: ${ph}`);
            this.pageWidth = pw;
            this.pageHeight = ph;
            this.ready = true;
        },
        setScale(scale) {
            this.scale = scale;
        },
        pdfLoaded(data) {
            this.setOffset();
            
            this.$emit('pageLoaded', {
                ...data, 
                offsetX: this.offsetX, 
                offsetY: this.offsetY,
                width: this.pdfWidth,
                height: this.pdfHeight,
                pageWidth: this.pageWidth,
                pageHeight: this.pageHeight,
                markerWidth: this.cueMarkerWidth,
                cueMarkerHeight: this.cueMarkerHeight
            });

            // this.displayCues.forEach((cue) => {
            //     if (cue.x > 1 || cue.y > 1) {
            //         cue.x /= this.pageWidth;
            //         if (cue.x > 1) cue.x = 1;
            //         cue.y /= this.pdfHeight;
            //         if (cue.y > 1) cue.y = 1;
            //         this.$store.dispatch('updateCue', cue);
            //     }
            // });
            // this.displayNotes.forEach((note) => {
            //     if (note.x > 1 || note.y > 1) {
            //         note.x /= this.pageWidth;
            //         if (note.x > 1) note.x = 1;
            //         note.y /= this.pdfHeight;
            //         if (note.y > 1) note.y = 1;
            //         this.$store.dispatch('updateUserNote', {note});
            //     }
            // });
            this.pageReady = true;
        },
        cueCreated(tempCue) {
            if (!this.currentLayer) {
                this.openDialog('no-layers-dialog');
                return;
            }
            if (this.locked || this.addingCue) {
                return;
            }

            const filter = this.editableLayers.filter(l => l.id === this.currentLayer.id);
            if (filter.length <= 0) {
                // return;
            }

            this.$nextTick(() => {
                // Do on next tick so the cue marker doesn't kill context menu handling
                this.addingCue = false;
                this.cueToAdd = {
                    ...tempCue,
                    title: this.currentLayer.title,
                    description: tempCue.text || '',
                    pageNumber: this.pageNumber
                };
                this.addCueX = tempCue.x + (this.offsetX - 25);
                this.addCueY = tempCue.y - 5;
                this.addingCue = true;
            });
        },
        addCue() {
            if (this.locked) return;

            // this.$emit('addedCue', this.cueToAdd);

            if (this.cueToAdd.x > 1 || this.cueToAdd.y > 1) {
                this.cueToAdd.x /= this.pageWidth;
                if (this.cueToAdd.x > 1) this.cueToAdd.x = 1;
                this.cueToAdd.y /= this.pageHeight;
                if (this.cueToAdd.y > 1) this.cueToAdd.y = 1;
            }
            this.$store.dispatch('addCue', this.cueToAdd);

            this.addingCue = false;
            this.cueToAdd = null;
            this.sortCues();
        },
        destroyCue(cueToDestroy) {
            // this.$emit('deletedCue', cueToDestroy);
            this.$store.dispatch('deletedCue', cueToDestroy);
        },
        updateCue(cueToUpdate) {
            // this.$emit('updatedCue', cueToUpdate);
            if (cueToUpdate.x > 1 || cueToUpdate.y > 1) {
                cueToUpdate.x /= this.pageWidth;
                if (cueToUpdate.x > 1) cueToUpdate.x = 1;
                cueToUpdate.y /= this.pageHeight;
                if (cueToUpdate.y > 1) cueToUpdate.y = 1;
            }
            this.$store.dispatch('updateCue', cueToUpdate);
        },
        updateNote(noteToUpdate) {
            if (noteToUpdate.x > 1 || noteToUpdate.y > 1) {
                noteToUpdate.x /= this.pageWidth;
                if (noteToUpdate.x > 1) noteToUpdate.x = 1;
                noteToUpdate.y /= this.pageHeight;
                if (noteToUpdate.y > 1) noteToUpdate.y = 1;
            }
            this.$store.dispatch('updateUserNote', {note: noteToUpdate});
        },
        addText() {
            if (this.locked) return;

            this.$store.dispatch('addUserNote', {
                note: this.cueToAdd,
                layer: this.currentLayer
            });
            this.addingCue = false;
            this.cueToAdd = null;
        },
        cancelAddCue() {
            this.addingCue = false;
            this.cueToAdd = null;
        },
        sortCues() {
            function comparator(a, b) {
                if (a.y === b.y) {
                    if (a.x === b.x) {
                        return 1;
                    } else if (a.x > b.x) {
                        return 1;
                    }
                    return 0;
                } else if (a.y > b.y) {
                    return 1;
                }
                return 0;
            }
            if (this.cues) {
                this.cues.sort(comparator);
                // this.$props.page.cues = this.cues;
            }
        },

        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        setOffset() {
            const page = this.$el.querySelector('.ql-pdf-page');
            if (page) {
                const bounds = page.getBoundingClientRect();

                this.pdfHeight = bounds.height;
                this.pdfWidth = bounds.width;
                this.offsetX = bounds.x;
                this.offsetY = bounds.y;

                this.$store.dispatch('updatePageOffset', bounds.x);
                // this.refreshCues();
            }
        },
        resize() {
            this.setOffset();
            if (this.pageReady) {
                this.$emit('pageLoaded', {
                    offsetX: this.offsetX, 
                    offsetY: this.offsetY,
                    width: this.pdfWidth,
                    height: this.pdfHeight,
                    pageWidth: this.pageWidth,
                    pageHeight: this.pageHeight,
                    markerWidth: this.cueMarkerWidth,
                    cueMarkerHeight: this.cueMarkerHeight
                });
            }
        },
        isCueEditable(cue) {
            const layer = this.layerByCue(cue);
            if (layer) {
                const filter = this.editableLayers.filter(l => l.id === layer.id);
                return filter.length > 0;
            }
            return false;
        },
        isNoteEditable(note) {
            const filter = this.editableLayers.filter(l => l.id === note.layer);
            return filter.length > 0;
        }
    },
    components: {
        'cue-position-marker': CuePositionMarker,
        'cue-selection-marker': CueSelectionMarker,
        'project-cues-add-cue-button': ProjectCuesAddCueButton,
        'project-cue-list': ProjectCueList,
        'resize-sensor': resizeSensor,
        'project-pdf-page': ProjectPdfPage,
        'project-note': ProjectNotesNote
    },
    mounted: function() {
        this.readyPage();
    }
};

export default ProjectPage;
</script>

<style lang="scss">

.marker-container, .ql-note-container {
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1001;
    pointer-events: none;
}

.ql-page {
    width: 100%;
    overflow: visible;
}
.ql-add-cue-button {
    position: absolute;
    z-index: 1001;
}

.ql-page-cover {
    background-color: #333;
}

.md-theme-default {
    .ql-page-cover {
        background-color: #a7a8a8;
    }
}

.ql-pdf-page {
    margin: 0 auto;
    position: relative;
    width: 100% !important;
    height: auto !important;
}

.night-mode {
    -webkit-filter: invert(88%) !important;
}

.disable-text {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}
</style>


// WEBPACK FOOTER //
// page.vue?8171f618